import React, { useState } from 'react';
import { X, Save, Wand2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const NODE_PALETTE = {
  input:     { color: 'hv-bg-policy',     label: 'INPUT' },
  agent:     { color: 'hv-bg-compliant',  label: 'AGENT' },
  decision:  { color: 'hv-bg-risk',       label: 'DECISION' },
  human:     { color: 'hv-bg-risk',       label: 'HUMAN' },
  transform: { color: 'hv-bg-policy',     label: 'TRANSFORM' },
  output:    { color: 'hv-bg-compliant',  label: 'OUTPUT' },
};

/**
 * Modal that shows full node visualization, allows editing the workflow's
 * name/description and individual node names + default messages, and exposes
 * Save/Improve/Duplicate actions.
 */
export function WorkflowDetailModal({
  workflow,
  isArabic,
  onClose,
  onSave,
  onImprove,
  onDuplicate,
  busy,
}) {
  const t = (en, ar) => (isArabic ? ar : en);
  const [draft, setDraft] = useState(workflow);
  const [feedback, setFeedback] = useState('');
  const [view, setView] = useState('overview');   // 'overview' | 'edit' | 'improve'

  const updateNode = (id, patch) => {
    setDraft((d) => ({
      ...d,
      nodes: d.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    }));
  };

  const updateRoot = (patch) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="workflow-detail-overlay"
    >
      <div
        className="hv-surface hv-border-strong hv-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="workflow-detail-modal"
      >
        <header className="flex items-center justify-between p-4 hv-border-b">
          <div>
            <h3 className="text-base font-semibold hv-text">{isArabic ? draft.name_ar : draft.name}</h3>
            <p className="text-xs hv-text-subtle font-mono">{draft.id} · {draft.category}</p>
          </div>
          <div className="flex items-center gap-2">
            <TabButton active={view === 'overview'} onClick={() => setView('overview')} testId="detail-tab-overview">
              {t('Overview', 'نظرة عامة')}
            </TabButton>
            <TabButton active={view === 'edit'} onClick={() => setView('edit')} testId="detail-tab-edit">
              {t('Edit', 'تحرير')}
            </TabButton>
            <TabButton active={view === 'improve'} onClick={() => setView('improve')} testId="detail-tab-improve">
              {t('Improve', 'تحسين')}
            </TabButton>
            <button onClick={onClose} className="hv-text-muted hover:hv-text ms-2" data-testid="workflow-detail-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {view === 'overview' && <OverviewTab workflow={draft} isArabic={isArabic} />}
          {view === 'edit' && (
            <EditTab
              draft={draft}
              isArabic={isArabic}
              updateRoot={updateRoot}
              updateNode={updateNode}
            />
          )}
          {view === 'improve' && (
            <ImproveTab
              feedback={feedback}
              setFeedback={setFeedback}
              isArabic={isArabic}
            />
          )}
        </div>

        <footer className="p-4 hv-border-t flex items-center justify-end gap-2">
          <button
            onClick={() => onDuplicate(draft.id)}
            disabled={busy}
            className="hv-btn text-sm"
            data-testid="workflow-detail-duplicate"
          >
            {t('Duplicate', 'تكرار')}
          </button>
          {view === 'edit' && (
            <button
              onClick={() => onSave(draft)}
              disabled={busy}
              className="hv-btn hv-btn-primary text-sm inline-flex items-center gap-1.5"
              data-testid="workflow-detail-save"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('Save changes', 'حفظ التعديلات')}
            </button>
          )}
          {view === 'improve' && (
            <button
              onClick={() => onImprove(draft.id, feedback)}
              disabled={busy}
              className="hv-btn hv-btn-primary text-sm inline-flex items-center gap-1.5"
              data-testid="workflow-detail-improve-run"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {t('Suggest improvements', 'اقترح تحسينات')}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={cn('text-xs px-3 py-1 rounded transition-colors',
        active ? 'hv-btn-primary' : 'hv-border hover:hv-border-strong hv-text-muted')}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function OverviewTab({ workflow, isArabic }) {
  const t = (en, ar) => (isArabic ? ar : en);
  return (
    <div className="space-y-4" data-testid="workflow-detail-overview">
      <p className="text-sm hv-text-muted">{isArabic ? workflow.description_ar : workflow.description}</p>

      <div className="space-y-1.5">
        <div className="text-xs hv-text-subtle uppercase tracking-wider mb-2">
          {t('Pipeline', 'خط الإنتاج')} ({workflow.nodes.length})
        </div>
        {workflow.nodes.map((node, idx) => {
          const palette = NODE_PALETTE[node.type] || NODE_PALETTE.transform;
          const isStart = node.id === workflow.start_node_id;
          return (
            <div
              key={node.id}
              className="flex items-start gap-3 hv-border rounded p-3"
              data-testid={`workflow-detail-node-${node.id}`}
            >
              <span className="hv-text-subtle text-xs font-mono mt-0.5">{String(idx + 1).padStart(2, '0')}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn('text-[10px] px-2 py-0.5 rounded uppercase tracking-wider', palette.color)}>
                    {palette.label}
                  </span>
                  {isStart && (
                    <span className="text-[10px] hv-text-subtle uppercase">start</span>
                  )}
                  <span className="text-sm font-semibold hv-text">{isArabic ? node.name_ar : node.name}</span>
                  <code className="text-[10px] hv-text-subtle font-mono ms-auto">{node.id}</code>
                </div>
                {node.config?.agent && (
                  <div className="text-xs hv-text-muted mt-1">
                    {t('agent', 'وكيل')}: <code className="hv-tag-policy">{node.config.agent}</code>
                    {node.config.task && <> · {t('task', 'مهمة')}: <code className="hv-tag-compliant">{node.config.task}</code></>}
                  </div>
                )}
                {node.config?.embedded_context && (
                  <div className="text-xs hv-text-subtle mt-1">
                    {t('embedded context', 'سياق مضمَّن')}: {Object.keys(node.config.embedded_context).join(', ')}
                  </div>
                )}
                {Array.isArray(node.next_nodes) && node.next_nodes.length > 0 && (
                  <div className="text-[11px] hv-text-subtle mt-1">
                    → {node.next_nodes.join(', ')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EditTab({ draft, isArabic, updateRoot, updateNode }) {
  const t = (en, ar) => (isArabic ? ar : en);
  return (
    <div className="space-y-4" data-testid="workflow-detail-edit">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={t('Name (English)', 'الاسم (إنجليزي)')} testId="edit-name">
          <input
            value={draft.name || ''}
            onChange={(e) => updateRoot({ name: e.target.value })}
            className="w-full hv-border hv-surface-alt rounded p-2 text-sm focus:outline-none focus:hv-border-strong"
          />
        </Field>
        <Field label={t('Name (Arabic)', 'الاسم (عربي)')} testId="edit-name-ar">
          <input
            value={draft.name_ar || ''}
            onChange={(e) => updateRoot({ name_ar: e.target.value })}
            className="w-full hv-border hv-surface-alt rounded p-2 text-sm focus:outline-none focus:hv-border-strong"
          />
        </Field>
      </div>

      <Field label={t('Description', 'الوصف')} testId="edit-description">
        <textarea
          rows={2}
          value={draft.description || ''}
          onChange={(e) => updateRoot({ description: e.target.value })}
          className="w-full hv-border hv-surface-alt rounded p-2 text-sm resize-none focus:outline-none focus:hv-border-strong"
        />
      </Field>

      <div className="text-xs hv-text-subtle uppercase tracking-wider">{t('Edit nodes', 'تحرير العقد')}</div>
      {draft.nodes.map((node) => (
        <div key={node.id} className="hv-border rounded p-3 space-y-2" data-testid={`edit-node-${node.id}`}>
          <div className="flex items-center justify-between">
            <code className="text-xs hv-text-subtle">{node.id} · {node.type}</code>
          </div>
          <Field label={t('Name', 'الاسم')} compact>
            <input
              value={node.name || ''}
              onChange={(e) => updateNode(node.id, { name: e.target.value })}
              className="w-full hv-border hv-surface-alt rounded p-1.5 text-sm focus:outline-none focus:hv-border-strong"
            />
          </Field>
          {(node.type === 'agent' || node.type === 'human') && (
            <Field label={node.type === 'human' ? t('Prompt', 'النص') : t('Default message', 'الرسالة الافتراضية')} compact>
              <textarea
                rows={2}
                value={(node.type === 'human' ? node.config?.prompt : node.config?.default_message) || ''}
                onChange={(e) =>
                  updateNode(node.id, {
                    config: {
                      ...(node.config || {}),
                      [node.type === 'human' ? 'prompt' : 'default_message']: e.target.value,
                    },
                  })
                }
                className="w-full hv-border hv-surface-alt rounded p-1.5 text-sm resize-none focus:outline-none focus:hv-border-strong"
              />
            </Field>
          )}
        </div>
      ))}
    </div>
  );
}

function ImproveTab({ feedback, setFeedback, isArabic }) {
  const t = (en, ar) => (isArabic ? ar : en);
  return (
    <div className="space-y-3" data-testid="workflow-detail-improve">
      <p className="text-sm hv-text-muted">
        {t(
          'Describe what you want improved. Bayenah will analyse this workflow + recent run history and propose an improved graph.',
          'صف ما الذي تريد تحسينه. ستحلل بيّنة هذا السير وسجلات التشغيل الأخيرة وتقترح رسماً محسَّناً.'
        )}
      </p>
      <textarea
        rows={5}
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder={t(
          'e.g., Add a GDPR compliance branch when European parties are present.',
          'مثال: أضف فرع امتثال للائحة GDPR عند وجود أطراف أوروبية.'
        )}
        className="w-full hv-border hv-surface-alt rounded p-3 text-sm resize-none focus:outline-none focus:hv-border-strong"
        data-testid="workflow-detail-improve-feedback"
      />
    </div>
  );
}

function Field({ label, children, compact, testId }) {
  return (
    <label className={cn('block', compact && 'space-y-1')} data-testid={testId}>
      <span className="text-[11px] hv-text-subtle uppercase tracking-wider block mb-1">{label}</span>
      {children}
    </label>
  );
}

export default WorkflowDetailModal;
